import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Business,
  CheckCircle,
  Error,
  Info,
  ArrowForward,
  ArrowBack,
  Check,
  Star,
} from '@mui/icons-material';
import PasswordStrengthMeter from '../../components/Auth/PasswordStrengthMeter';
import { evaluatePassword } from '../../utils/passwordStrength';

interface Plan {
  id: string;
  name: string;
  price: number;
  currency: string;
  billingCycle: string;
  description: string;
  features: string[];
  limits: {
    users: number;
    cases: number;
    clients: number;
    documents: number;
    storageGB: number;
  };
}

interface Feature {
  name: string;
  demo: boolean;
  basic: boolean;
  professional: boolean;
  enterprise: boolean;
}

const TenantRegistrationPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);

  const [formData, setFormData] = useState({
    firmName: '',
    subdomain: '',
    adminEmail: '',
    adminPassword: '',
    adminFirstName: '',
    adminLastName: '',
    plan: 'demo', // Default to demo for testing
    industry: '',
    firmSize: '1-5',
    phone: '',
    website: '',
  });

  const theme = useTheme();

  const steps = ['Choose Plan', 'Firm Information', 'Admin Account', 'Review & Create'];

  useEffect(() => {
    fetchPlans();
    fetchFeatures();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch('/api/tenant-registration/plans');
      const data = await response.json();
      if (data.success) {
        setPlans(data.data);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await fetch('/api/tenant-registration/features');
      const data = await response.json();
      if (data.success) {
        setFeatures(data.data);
      }
    } catch (error) {
      console.error('Error fetching features:', error);
    }
  };

  const checkSubdomain = async (subdomain: string) => {
    if (subdomain.length < 3) return;

    setIsCheckingSubdomain(true);
    try {
      const response = await fetch('/api/tenant-registration/check-subdomain', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subdomain }),
      });

      const data = await response.json();
      setSubdomainAvailable(data.available);
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setSubdomainAvailable(false);
    } finally {
      setIsCheckingSubdomain(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/tenant-registration/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        // Registration successful
        window.location.href = data.data.loginUrl;
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (error) {
      setError('An error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'subdomain') {
      const cleanSubdomain = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, subdomain: cleanSubdomain }));
      if (cleanSubdomain.length >= 3) {
        checkSubdomain(cleanSubdomain);
      } else {
        setSubdomainAvailable(null);
      }
    }
  };

  const renderPlanSelection = () => (
    <Grid container spacing={3}>
      {plans.map((plan) => (
        <Grid item xs={12} md={4} key={plan.id}>
          <Card
            sx={{
              height: '100%',
              cursor: 'pointer',
              border: formData.plan === plan.id ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
              '&:hover': {
                borderColor: theme.palette.primary.main,
              },
              ...(plan.id === 'demo' && {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '& .MuiTypography-root': {
                  color: 'white',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
                '& .MuiCheckCircle-root': {
                  color: 'white',
                },
                '& .MuiCheck-root': {
                  color: 'white',
                },
              }),
            }}
            onClick={() => handleInputChange('plan', plan.id)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{plan.name}</Typography>
                {formData.plan === plan.id && <CheckCircle color="primary" />}
                {plan.id === 'demo' && (
                  <Chip
                    label="RECOMMENDED"
                    color="warning"
                    size="small"
                    sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
                  />
                )}
              </Box>
              
              <Typography variant="h4" color="primary" gutterBottom>
                ${plan.price}
                <Typography component="span" variant="body2" color="text.secondary">
                  /{plan.billingCycle}
                </Typography>
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {plan.description}
              </Typography>
              
              {plan.id === 'demo' && (
                <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    <strong>30-day full access</strong> - Experience all features before choosing your plan
                  </Typography>
                </Alert>
              )}
              
              <List dense>
                {plan.features.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Check fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderFirmInformation = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Firm Name"
          value={formData.firmName}
          onChange={(e) => handleInputChange('firmName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Subdomain"
          value={formData.subdomain}
          onChange={(e) => handleInputChange('subdomain', e.target.value)}
          required
          helperText={
            isCheckingSubdomain ? 'Checking availability...' :
            subdomainAvailable === true ? 'Subdomain is available' :
            subdomainAvailable === false ? 'Subdomain is taken' :
            'Choose a unique subdomain for your firm'
          }
          InputProps={{
            endAdornment: isCheckingSubdomain ? <CircularProgress size={20} /> : null,
          }}
        />
        {formData.subdomain && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Your dashboard will be available at: <strong>{formData.subdomain}.argistech.com</strong>
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Industry"
          value={formData.industry}
          onChange={(e) => handleInputChange('industry', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <FormLabel>Firm Size</FormLabel>
          <RadioGroup
            value={formData.firmSize}
            onChange={(e) => handleInputChange('firmSize', e.target.value)}
            row
          >
            <FormControlLabel value="1-5" control={<Radio />} label="1-5" />
            <FormControlLabel value="6-10" control={<Radio />} label="6-10" />
            <FormControlLabel value="11-25" control={<Radio />} label="11-25" />
            <FormControlLabel value="26-50" control={<Radio />} label="26-50" />
            <FormControlLabel value="50+" control={<Radio />} label="50+" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Phone"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Website"
          value={formData.website}
          onChange={(e) => handleInputChange('website', e.target.value)}
        />
      </Grid>
    </Grid>
  );

  const renderAdminAccount = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="First Name"
          value={formData.adminFirstName}
          onChange={(e) => handleInputChange('adminFirstName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Last Name"
          value={formData.adminLastName}
          onChange={(e) => handleInputChange('adminLastName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={formData.adminEmail}
          onChange={(e) => handleInputChange('adminEmail', e.target.value)}
          required
          helperText="This will be your admin login email"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={formData.adminPassword}
          onChange={(e) => handleInputChange('adminPassword', e.target.value)}
          required
          helperText="Min 12 characters with upper, lower, number, and special character"
        />
        <PasswordStrengthMeter password={formData.adminPassword} />
      </Grid>
    </Grid>
  );

  const renderReview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Review Your Registration
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Firm Information
            </Typography>
            <Typography><strong>Name:</strong> {formData.firmName}</Typography>
            <Typography><strong>Subdomain:</strong> {formData.subdomain}.argistech.com</Typography>
            <Typography><strong>Industry:</strong> {formData.industry || 'Not specified'}</Typography>
            <Typography><strong>Size:</strong> {formData.firmSize} employees</Typography>
            <Typography><strong>Phone:</strong> {formData.phone || 'Not specified'}</Typography>
            <Typography><strong>Website:</strong> {formData.website || 'Not specified'}</Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Admin Account
            </Typography>
            <Typography><strong>Name:</strong> {formData.adminFirstName} {formData.adminLastName}</Typography>
            <Typography><strong>Email:</strong> {formData.adminEmail}</Typography>
            <Typography><strong>Role:</strong> Administrator</Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Selected Plan
            </Typography>
            {plans.find(p => p.id === formData.plan) && (
              <Box>
                <Typography variant="h5" color="primary">
                  {plans.find(p => p.id === formData.plan)?.name} Plan
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {plans.find(p => p.id === formData.plan)?.description}
                </Typography>
                <List dense sx={{ mt: 2 }}>
                  {plans.find(p => p.id === formData.plan)?.features.map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <Check fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderPlanSelection();
      case 1:
        return renderFirmInformation();
      case 2:
        return renderAdminAccount();
      case 3:
        return renderReview();
      default:
        return 'Unknown step';
    }
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return formData.plan;
      case 1:
        return formData.firmName && formData.subdomain && subdomainAvailable === true;
      case 2:
        return formData.adminFirstName && formData.adminLastName && 
               formData.adminEmail && evaluatePassword(formData.adminPassword).valid;
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Business sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom>
          Register Your Law Firm
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Get your own secure, isolated legal dashboard in minutes
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent>
          {getStepContent(activeStep)}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        
        <Box>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading || !isStepValid(activeStep)}
              startIcon={isLoading ? <CircularProgress size={20} /> : <Check />}
            >
              {isLoading ? 'Creating Your Dashboard...' : 'Create Dashboard'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid(activeStep)}
              endIcon={<ArrowForward />}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>

      {activeStep === 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Feature Comparison
          </Typography>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Feature
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Demo
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Basic
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Professional
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Enterprise
                  </Typography>
                </Grid>
                
                {features.map((feature, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body2">{feature.name}</Typography>
                    </Grid>
                    <Grid item xs={12} md={2} sx={{ 
                      backgroundColor: feature.demo ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                      borderRadius: 1,
                      p: 0.5
                    }}>
                      {feature.demo ? (
                        <Check color="primary" sx={{ color: 'primary.main' }} />
                      ) : (
                        <Error color="disabled" />
                      )}
                    </Grid>
                    <Grid item xs={12} md={2}>
                      {feature.basic ? <Check color="primary" /> : <Error color="disabled" />}
                    </Grid>
                    <Grid item xs={12} md={3}>
                      {feature.professional ? <Check color="primary" /> : <Error color="disabled" />}
                    </Grid>
                    <Grid item xs={12} md={3}>
                      {feature.enterprise ? <Check color="primary" /> : <Error color="disabled" />}
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
              
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'primary.main', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
                  <strong>🎉 Demo Plan:</strong> Full access to all Enterprise features for 30 days. Perfect for testing and evaluation.
                  <br />
                  <small>After 30 days, automatically reverts to Basic plan unless upgraded.</small>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default TenantRegistrationPage; 